import { parseIntervalInput } from "../../../../../components/inputs/IntervalInput";
import { parseRootFractionInput } from "../../../../../components/inputs/RootFractionInput";

/* ========================================================================== */
/* STAŁE I PROSTE POMOCNICZE                                                  */
/* ========================================================================== */

export const TOL = 1e-9;
export const R2 = 5; // |y| <= 5

export function eq(a, b) {
  return Math.abs(a - b) < TOL;
}

export function randInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

export function integerArgumentsInInterval(interval) {
  if (!interval) return [];

  let first = Math.ceil(interval.left);
  let last = Math.floor(interval.right);

  if (!interval.leftClosed && eq(first, interval.left)) first += 1;
  if (!interval.rightClosed && eq(last, interval.right)) last -= 1;

  const values = [];
  for (let x = first; x <= last; x += 1) values.push(x);
  return values;
}

export function normalizeNumberText(txt) {
  return String(txt ?? "")
    .replace(/[−–—]/g, "-")
    .replace(/\u00A0|\u202F|\u2009|\u200A|\u200B/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function parseNumberFromAny(v) {
  if (v == null) return NaN;

  if (typeof v === "object") {
    if (Number.isFinite(v.parsed)) return v.parsed;
    if (typeof v.raw === "string") {
      const s = normalizeNumberText(v.raw).replace(",", ".");
      return parseRootFractionInput(s);
    }
  }

  if (typeof v === "number") return v;

  if (typeof v === "string") {
    const s = normalizeNumberText(v).replace(",", ".");
    return parseRootFractionInput(s);
  }

  return NaN;
}

export function getFractionRaw(v) {
  if (!v) return "";
  if (typeof v === "string") return v;
  if (typeof v === "object" && typeof v.raw === "string") return v.raw ?? "";
  return "";
}

export function getIntervalRaw(obj, key) {
  const val = obj?.[key];
  if (!val) return "";
  if (typeof val === "string") return val;
  if (typeof val === "object") {
    if (typeof val.raw === "string") return val.raw;
    // legacy / nested snapshot shape: { raw: { raw: "...", parsed: ... }, parsed: ... }
    if (val.raw && typeof val.raw === "object" && typeof val.raw.raw === "string") {
      return val.raw.raw;
    }
  }
  return "";
}

/**
 * Parsuje dokładnie JEDEN przedział z inputu (np. [1,3), (−∞,2] itp.)
 * Zwraca obiekt { left, right, leftClosed, rightClosed } lub null.
 */
export function parseSingleInterval(raw) {
  if (!raw) return null;

  const s = String(raw).trim();
  if (!s) return null;

  const parsed = parseIntervalInput(s);

  if (
    !parsed ||
    parsed.kind !== "intervals" ||
    !parsed.intervals ||
    parsed.intervals.length !== 1
  ) {
    return null;
  }

  const I = parsed.intervals[0];

  if (!Number.isFinite(I.left) || !Number.isFinite(I.right)) return null;
  if (!(I.left <= I.right)) return null; // dopuszczamy [a,a]

  return I;
}

/**
 * Parsuje SUMĘ przedziałów – np.
 *  (-3,1] ∪ (2,4)  albo  (-3,1];(2,4)
 * Zwraca tablicę przedziałów [{left,right,leftClosed,rightClosed}, ...] lub null.
 */
export function parseIntervalsUnion(raw) {
  if (!raw) return null;

  const s = String(raw).trim();
  if (!s) return null;

  const parsed = parseIntervalInput(s);

  if (
    !parsed ||
    parsed.kind !== "intervals" ||
    !parsed.intervals ||
    parsed.intervals.length === 0
  ) {
    return null;
  }

  const out = [];

  for (const I of parsed.intervals) {
    if (
      !I ||
      !Number.isFinite(I.left) ||
      !Number.isFinite(I.right) ||
      !(I.left <= I.right)
    ) {
      return null;
    }
    out.push({
      left: I.left,
      right: I.right,
      leftClosed: !!I.leftClosed,
      rightClosed: !!I.rightClosed,
    });
  }

  return out;
}

/**
 * Czy surowy zapis przedziału/sumy przedziałów ma odwróconą kolejność końców
 * (np. (4;0), [5;2], itp.).
 */
export function intervalHasInvalidOrder(raw) {
  if (!raw) return false;
  const parsed = parseIntervalInput(String(raw).trim());
  return !!parsed && parsed.kind === "invalid_order";
}

/**
 * Porównuje dwie tablice przedziałów (kolejność ma znaczenie).
 */
export function sortIntervalsByLeft(intervals) {
  if (!Array.isArray(intervals)) return [];
  return [...intervals].sort((A, B) => {
    if (!A || !B) return 0;
    if (A.left !== B.left) return A.left - B.left;
    return A.right - B.right;
  });
}

export function intervalsEqualList(expected, actual) {
  if (!Array.isArray(expected) || !Array.isArray(actual)) return false;
  if (expected.length !== actual.length) return false;

  const E = sortIntervalsByLeft(expected);
  const A = sortIntervalsByLeft(actual);

  for (let i = 0; i < E.length; i++) {
    const e = E[i];
    const a = A[i];
    if (
      !e ||
      !a ||
      e.left !== a.left ||
      e.right !== a.right ||
      !!e.leftClosed !== !!a.leftClosed ||
      !!e.rightClosed !== !!a.rightClosed
    ) {
      return false;
    }
  }
  return true;
}



/** Ładne formatowanie ax + b: 1x -> x, -1x -> -x, bez + 0. */
export function formatLinear(a, b) {
  let left;
  if (a === 1) left = "x";
  else if (a === -1) left = "-x";
  else left = `${a}x`;

  if (Math.abs(b) < TOL) return left;

  if (b > 0) return `${left} + ${b}`;
  return `${left} ${b}`; // b już ma minus
}

export function intervalObjectToLatex(I) {
  if (!I) return "";
  const lBrace = I.leftClosed ? "\\left[" : "\\left(";
  const rBrace = I.rightClosed ? "\\right]" : "\\right)";
  return `${lBrace}${I.left},\\, ${I.right}${rBrace}`;
}

export function ineqSignToLatex(sign) {
  if (sign === "<=") return "\\le";
  if (sign === ">=") return "\\ge";
  return sign;
}

/* ========================================================================== */
/* ROZWIĄZANIE NIERÓWNOŚCI f(x) <= 0 (OBECNA LOGIKA – UŻYWAMY PO PRZESUNIĘCIU) */
/* ========================================================================== */

export function computeIneqLE0Solution(a1, b1, interval1, a2, b2, interval2) {
  // f tutaj oznacza już przesuniętą funkcję: g(x) = f(x) - c,
  // więc szukamy rozwiązań g(x) <= 0
  const f = (x) =>
    x <= interval1.right + TOL ? a1 * x + b1 : a2 * x + b2;

  const Dleft = interval1.left;
  const Dright = interval2.right;

  const roots = [];

  function pushRoot(a, b, I) {
    if (Math.abs(a) < TOL) return;
    const z = -b / a;
    if (z < I.left - TOL || z > I.right + TOL) return;
    roots.push(z);
  }

  pushRoot(a1, b1, interval1);
  pushRoot(a2, b2, interval2);
  roots.sort((u, v) => u - v);

  const fLeft = a1 * Dleft + b1;
  const fRight = a2 * Dright + b2;

  function closedAt(x) {
    if (eq(x, interval1.left)) return interval1.leftClosed;
    if (eq(x, interval1.right)) return interval1.rightClosed;
    if (eq(x, interval2.left)) return interval2.leftClosed;
    if (eq(x, interval2.right)) return interval2.rightClosed;
    // zero w środku odcinka – należy do rozwiązania g(x) <= 0
    return true;
  }

  function normalizeEndpoint(x) {
    const r = Math.round(x);
    return Math.abs(r - x) < TOL ? r : x;
  }

  // 0 miejsc zerowych
  if (roots.length === 0) {
    if (fLeft <= TOL && fRight <= TOL) {
      return {
        left: normalizeEndpoint(Dleft),
        right: normalizeEndpoint(Dright),
        leftClosed: interval1.leftClosed,
        rightClosed: interval2.rightClosed,
      };
    }
    return null; // nigdzie nie jest <= 0
  }

  // 1 miejsce zerowe
  if (roots.length === 1) {
    const z = roots[0];

    let left, right, leftClosed, rightClosed;

    if (fLeft <= TOL && fRight > TOL) {
      // g <= 0 od lewego końca do zera
      left = Dleft;
      leftClosed = interval1.leftClosed;
      right = z;
      rightClosed = closedAt(z);
    } else if (fLeft > TOL && fRight <= TOL) {
      // g <= 0 od zera do prawego końca
      left = z;
      leftClosed = closedAt(z);
      right = Dright;
      rightClosed = interval2.rightClosed;
    } else if (fLeft <= TOL && fRight <= TOL) {
      // cały przedział <= 0
      left = Dleft;
      leftClosed = interval1.leftClosed;
      right = Dright;
      rightClosed = interval2.rightClosed;
    } else {
      // oba końce > 0, ale jest jedno zero – rozwiązaniem jest sam punkt
      left = z;
      right = z;
      leftClosed = closedAt(z);
      rightClosed = closedAt(z);
    }

    left = normalizeEndpoint(left);
    right = normalizeEndpoint(right);
    if (!(left <= right + TOL)) return null;

    return { left, right, leftClosed, rightClosed };
  }

  // >=2 miejsca zerowe – bierzemy skrajne; zakładamy "środek pod osią"
  const leftZero = roots[0];
  const rightZero = roots[roots.length - 1];
  const mid = (leftZero + rightZero) / 2;
  const fMid = f(mid);

  // Jeśli g(mid) > 0, rozwiązanie byłoby sumą dwóch przedziałów – obecnie
  // logika V-kształtu tego nie obsługuje i zwracamy null.
  if (fMid > TOL) {
    return null;
  }

  let left = leftZero;
  let right = rightZero;
  let leftClosed = closedAt(leftZero);
  let rightClosed = closedAt(rightZero);

  left = normalizeEndpoint(left);
  right = normalizeEndpoint(right);
  if (!(left <= right + TOL)) return null;

  return { left, right, leftClosed, rightClosed };
}

/* ========================================================================== */
/* GENERATOR KSZTAŁTU "V" W OKNIE [-6,6]×[-6,6]                               */
/* ========================================================================== */

export function tryGenerateV() {
  for (let attempt = 0; attempt < 400; attempt++) {
    const xL = Math.random() < 0.5 ? -5 : -4;
    const xR = Math.random() < 0.5 ? 4 : 5;

    const hCandidates = [-3, -2, -1, 0, 1, 2, 3].filter(
      (x) => x > xL + 1 && x < xR - 1
    );
    if (!hCandidates.length) continue;
    const h = hCandidates[randInt(0, hCandidates.length - 1)];

    const mRight = randInt(1, 2);
    const mLeftAbs = randInt(1, 2);

    const v = -randInt(1, 4); // wierzchołek poniżej osi OX

    const m1 = -mLeftAbs;
    const m2 = mRight;

    const f1 = (x) => m1 * (x - h) + v;
    const f2 = (x) => m2 * (x - h) + v;

    const yL = f1(xL);
    const yR = f2(xR);

    const maxAbs = Math.max(Math.abs(yL), Math.abs(v), Math.abs(yR));
    if (maxAbs > R2) continue;

    const a1 = m1;
    const b1 = v - m1 * h;
    const a2 = m2;
    const b2 = v - m2 * h;

    if (Math.abs(b1) < TOL || Math.abs(b2) < TOL) continue;

    const endpointPattern = randInt(0, 3);
    const leftClosedEndpoint =
      endpointPattern === 1 || endpointPattern === 2;
    const rightClosedEndpoint =
      endpointPattern === 0 || endpointPattern === 2;

    const interval1 = {
      left: xL,
      right: h,
      leftClosed: leftClosedEndpoint,
      rightClosed: true,
    };
    const interval2 = {
      left: h,
      right: xR,
      leftClosed: false,
      rightClosed: rightClosedEndpoint,
    };

    const zeros = [];

    const pushZero = (a, b, I) => {
      if (Math.abs(a) < TOL) return;
      const z = -b / a;

      let inside = true;
      if (I.leftClosed) inside = inside && z >= I.left - TOL;
      else inside = inside && z > I.left + TOL;

      if (I.rightClosed) inside = inside && z <= I.right + TOL;
      else inside = inside && z < I.right - TOL;

      if (inside) zeros.push(z);
    };

    pushZero(a1, b1, interval1);
    pushZero(a2, b2, interval2);

    if (zeros.length !== 1) continue;
    const zeroX = zeros[0];

    // Argumenty używane w obliczaniu wartości funkcji mają leżeć wewnątrz
    // odpowiednich gałęzi, ale nie mogą być punktem załamania wykresu.
    const leftInts = integerArgumentsInInterval(interval1).filter((x) => !eq(x, h));
    const rightInts = integerArgumentsInInterval(interval2).filter((x) => !eq(x, h));
    if (!leftInts.length || !rightInts.length) continue;

    const exprX1 = leftInts[randInt(0, leftInts.length - 1)];
    const exprX2 = rightInts[randInt(0, rightInts.length - 1)];

    const kCandidates = [2, 3, 4, 5];
    const k = kCandidates[randInt(0, kCandidates.length - 1)];

    const f = (x) => (x <= h ? f1(x) : f2(x));
    const exprValue = f(exprX1) + k * f(exprX2);

    const minY = Math.min(yL, v, yR);
    const maxY = Math.max(yL, v, yR);

    const rangeLeft = minY;
    const rangeLeftClosed = true;

    const yMax = maxY;
    const maxAtLeft = eq(yL, yMax);
    const maxAtRight = eq(yR, yMax);

    let rangeRightClosed = false;
    if (maxAtLeft && interval1.leftClosed) rangeRightClosed = true;
    if (maxAtRight && interval2.rightClosed) rangeRightClosed = true;

    const range = {
      left: rangeLeft,
      right: maxY,
      leftClosed: rangeLeftClosed,
      rightClosed: rangeRightClosed,
    };

    const Dleft = interval1.left;
    const Dright = interval2.right;

    const intMin = Math.ceil(range.left);
    const intMax = Math.floor(range.right);

    const isInt = (x) => Math.abs(x - Math.round(x)) < TOL;

    const goodThresholdsIntegerEnds = [];

    for (let t = intMin; t <= intMax; t++) {
      const sol = computeIneqLE0Solution(
        a1,
        b1 - t,
        interval1,
        a2,
        b2 - t,
        interval2
      );
      if (!sol) continue;

      const length = sol.right - sol.left;
      if (!(length >= 1 - TOL)) continue;

      const isWholeDomainApprox =
        eq(sol.left, Dleft) && eq(sol.right, Dright);

      if (isWholeDomainApprox) continue;

      if (isInt(sol.left) && isInt(sol.right)) {
        goodThresholdsIntegerEnds.push({ t, sol });
      }
    }

    if (!goodThresholdsIntegerEnds.length) continue;

    const chosen =
      goodThresholdsIntegerEnds[randInt(0, goodThresholdsIntegerEnds.length - 1)];
    const ineqThreshold = chosen.t;
    const ineqSolution = chosen.sol;

    const ineqSign = "<=";

    const atBorder = [xL, xR].some((val) =>
      [-5, -4, 4, 5].some((b) => eq(val, b))
    );
    if (!atBorder) continue;

    const eps = 0;
    const xLline = xL + eps;
    const yLline = f1(xLline);
    const xRline = xR - eps;
    const yRline = f2(xRline);

    const AmarkerSymbol = leftClosedEndpoint ? undefined : "circle-open";
    const CmarkerSymbol = rightClosedEndpoint ? undefined : "circle-open";

    const points = {
      A: {
        x: xL,
        y: yL,
        markerSymbol: AmarkerSymbol,
        showLabel: false,
      },
      AL: {
        x: xLline,
        y: yLline,
        showDot: false,
        showMarker: false,
        showLabel: false,
        label: "",
      },
      B: {
        x: h,
        y: v,
        showDot: false,
        showMarker: false,
        showLabel: false,
      },
      CR: {
        x: xRline,
        y: yRline,
        showDot: false,
        showMarker: false,
        showLabel: false,
        label: "",
      },
      C: {
        x: xR,
        y: yR,
        markerSymbol: CmarkerSymbol,
        showLabel: false,
      },
    };

    return {
      shape: "V",
      a1,
      b1,
      interval1,
      a2,
      b2,
      interval2,
      points,
      zeroX,
      exprX1,
      exprX2,
      k,
      exprValue,
      range,
      ineqSign,
      ineqThreshold,
      ineqSolution,
      h,
    };
  }

  return null;
}

/* ========================================================================== */
/* ODBICIE WZGLĘDEM OSI OX (kształt "^")                                      */
/* ========================================================================== */

export function flipToHat(dataV) {
  const {
    a1,
    b1,
    interval1,
    a2,
    b2,
    interval2,
    points,
    zeroX,
    exprX1,
    exprX2,
    k,
    exprValue,
    range,
    ineqSolution,
    ineqSign,
    ineqThreshold,
    h,
  } = dataV;

  let newSign;
  if (ineqSign === "<") newSign = ">";
  else if (ineqSign === "<=") newSign = ">=";
  else newSign = ineqSign;

  return {
    shape: "^",
    a1: -a1,
    b1: -b1,
    interval1,
    a2: -a2,
    b2: -b2,
    interval2,
    points: {
      A: {
        x: points.A.x,
        y: -points.A.y,
        markerSymbol: points.A.markerSymbol,
        showLabel: false,
      },
      AL: {
        x: points.AL.x,
        y: -points.AL.y,
        showDot: false,
        showMarker: false,
        showLabel: false,
        label: "",
      },
      B: {
        x: points.B.x,
        y: -points.B.y,
        showDot: false,
        showMarker: false,
        showLabel: false,
      },
      CR: {
        x: points.CR.x,
        y: -points.CR.y,
        showDot: false,
        showMarker: false,
        showLabel: false,
        label: "",
      },
      C: {
        x: points.C.x,
        y: -points.C.y,
        markerSymbol: points.C.markerSymbol,
        showLabel: false,
      },
    },
    zeroX,
    exprX1,
    exprX2,
    k,
    exprValue: -exprValue,
    range: {
      left: -range.right,
      right: -range.left,
      leftClosed: range.rightClosed,
      rightClosed: range.leftClosed,
    },
    ineqSign: newSign,
    ineqThreshold: -ineqThreshold,
    ineqSolution: { ...ineqSolution },
    h,
  };
}
